/**
 * RowActions — per-row "…" menu (UI ruling 34, extended by ruling 52's 2.5
 * items; menu order and copy match the Figma row-actions frames verbatim).
 *
 * "Send first touch" is RENDERED ONLY when the row can actually take one —
 * sourced, has a contact, and nothing already queued or sending. It never
 * shows once the entry has a thread (contacted+): that's what Open thread
 * and the reply/negotiation actions are for.
 *
 * "View profile" is always available, canWrite or not — the read surface a
 * staff member reaches for regardless of where the entry sits.
 *
 * "Open thread" only makes sense once there is a thread — contacted,
 * replied, agreed or closed. Read-only staff still get it: the thread is the
 * record of what was sent and why anything stalled.
 *
 * "Edit contact" / "Find contact" (2.3/2.4) have moved out of this menu —
 * ContactCell's own inline affordances are where they live now, not
 * duplicated here (ruling 79).
 *
 * Everything else needs outreach:write. "Suppress creator" only shows once
 * there's a contact to suppress on a thread row; "Remove from suppression"
 * shows on any suppressed row, sourced or in-thread, in its place.
 * "Remove from campaign" (ruling 49 — never a hard delete on the BE) is
 * always last and destructive-styled.
 */
import {
  ArrowClockwise,
  ArrowCounterClockwise,
  ArrowLineUp,
  CheckCircle,
  Clock,
  DotsThree,
  Envelope,
  Export,
  MinusCircle,
  PaperPlaneTilt,
  Play,
  Stop,
  Trash,
  User,
  Warning,
} from '../../../design-system/icons';
import { followupPauseAction } from './followupPauseUi';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import type { OutreachRow } from '@discovr/types';
import { canMoveToBrand, canNotUnsubscribe, canReopenRow, hasRateToHandOff, hasThread, IN_FLIGHT_STATUSES, isFirstTouchDraft, isInOutreach, isPermanentOptOut, removeRefusal, reopenRefusal } from './handoffStates';
import { cn } from '../../../lib/utils';
import { RETRY_BUSY_TITLE, retryTarget } from './retryLabels';
import { ROW_MENU } from './menuStyles';

const REFUSED_ITEM = 'flex-wrap';

function RefusalReason({ text }: { text: string }) {
  return <span data-refusal className="basis-full pl-7 text-caption-regular text-muted-foreground">{text}</span>;
}

const ICON_BUTTON =
  'flex size-8 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground';

export type FirstTouchBlocker = 'no_contact' | 'already_contacted' | 'in_flight' | 'suppressed';

/** Go #711: a first touch that went out, or is going, makes the creator Contacted. A failed one sent no mail, so the wizard may redo it (R16-5). */
const FIRST_TOUCH_STARTED: readonly string[] = ['sending', 'sent', 'unknown_delivery'];

export function firstTouchBlocker(
  row: Pick<OutreachRow, 'status' | 'outreachState' | 'hasContact' | 'email' | 'emailOverride' | 'lastMessage' | 'suppressed' | 'firstTouch'>,
): FirstTouchBlocker | null {
  if (!isInOutreach(row) || row.outreachState !== 'sourced') return 'already_contacted';
  const firstTouch = row.firstTouch?.status;
  if (firstTouch && FIRST_TOUCH_STARTED.includes(firstTouch)) return 'already_contacted';
  if (firstTouch === 'scheduled' || firstTouch === 'queued') return 'in_flight';
  if (!row.emailOverride && !row.hasContact) return 'no_contact';
  if (row.suppressed) return 'suppressed';
  // A live send owns the row until it settles. An unsent draft stays eligible: the wizard approves it (spec §3).
  if (IN_FLIGHT_STATUSES.includes(row.lastMessage?.status ?? '')) return 'in_flight';
  return null;
}

export function canSendFirstTouch(row: OutreachRow): boolean {
  return firstTouchBlocker(row) === null;
}

export interface RowActionsProps {
  row: OutreachRow;
  canWrite: boolean;
  onOpenRow: (entryId: string) => void;
  onSendFirstTouch: (entryIds: string[]) => void;
  onViewProfile: (entryId: string) => void;
  onMarkRead: (entryId: string) => void;
  onMarkUnread: (entryId: string) => void;
  onPauseFollowups: (entryId: string) => void;
  onResumeFollowups: (entryId: string) => void;
  onSendToBrand: (entryId: string) => void;
  onSuppress: (entryId: string) => void;
  onUnsuppress: (entryId: string) => void;
  onRemove: (entryIds: string[]) => void;
  onClearEscalation?: (entryId: string) => void;
  onReopen?: (entryId: string) => void;
  onNotUnsubscribe?: (entryId: string) => void;
  /** E9-54: hands up the live first-touch draft's message id (admin-only route). */
  onDiscardDraft?: (entryId: string, messageId: number) => void;
  /** U6: hands up the message id the resend route needs. */
  onRetry?: (entryId: string, messageId: number) => void;
  /** E9-172: Stop on a queued or held message, as the thread drawer does. */
  onStop?: (entryId: string, messageId: number) => void;
  retryBusy?: boolean;
}

export function RowActions({
  row,
  canWrite,
  onOpenRow,
  onSendFirstTouch,
  onViewProfile,
  onMarkRead,
  onMarkUnread,
  onPauseFollowups,
  onResumeFollowups,
  onSendToBrand,
  onSuppress,
  onUnsuppress,
  onRemove,
  onClearEscalation,
  onReopen,
  onNotUnsubscribe,
  onDiscardDraft,
  onRetry,
  onStop,
  retryBusy,
}: RowActionsProps) {
  const threadRow = hasThread(row);
  const pauseAction = followupPauseAction(row);
  const paused = pauseAction === 'resume';
  const canUnsuppress = canWrite && row.suppressed && row.suppressionId !== undefined && !isPermanentOptOut(row);
  const canSuppress = canWrite && threadRow && !row.suppressed && row.hasContact;
  // A refused row keeps the item, disabled, with the reason as a visible caption (E9-37).
  const removeBlocked = removeRefusal(row);
  const canDelete = canWrite;
  const reopenBlocked = reopenRefusal(row);
  const liveDraft = row.lastMessage && isFirstTouchDraft(row.lastMessage.kind, row.lastMessage.status) ? row.lastMessage : null;
  const canDiscardDraft = canWrite && !!onDiscardDraft && liveDraft !== null;
  const retry = canWrite && onRetry ? retryTarget(row) : null;
  const stoppable = !!onStop && (retry?.status === 'queued' || retry?.status === 'held');
  const scheduledFirstTouch = row.firstTouch?.status === 'scheduled' || row.firstTouch?.status === 'queued';
  const recoveryLabel = retry?.status === 'failed' || retry?.status === 'unknown_delivery' ? 'Retry' : 'Send now';

  return (
    <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button type="button" aria-label={`Row actions for ${row.username}`} className={ICON_BUTTON}>
            <DotsThree size={20} weight="bold" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className={ROW_MENU}>
          {canWrite && !threadRow && canSendFirstTouch(row) && (
            <DropdownMenuItem
              aria-label={`Send first touch for ${row.username}`}
              onSelect={() => onSendFirstTouch([row.entryId])}
            >
              <PaperPlaneTilt size={14} />
              Send first touch
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onSelect={() => onViewProfile(row.entryId)}>
            <User size={14} />
            View profile
          </DropdownMenuItem>
          {(threadRow || scheduledFirstTouch) && (
            <DropdownMenuItem aria-label={`Open thread for ${row.username}`} onSelect={() => onOpenRow(row.entryId)}>
              <Envelope size={14} />
              Open thread
            </DropdownMenuItem>
          )}
          {retry && (
            <DropdownMenuItem
              disabled={retryBusy}
              title={retryBusy ? RETRY_BUSY_TITLE : undefined}
              onSelect={() => onRetry?.(row.entryId, retry.messageId)}
            >
              <ArrowClockwise size={14} />
              {recoveryLabel}
            </DropdownMenuItem>
          )}
          {stoppable && retry && (
            <DropdownMenuItem
              aria-label={`Stop sending to ${row.username}`}
              onSelect={() => onStop?.(row.entryId, retry.messageId)}
            >
              <Stop size={14} />
              Stop
            </DropdownMenuItem>
          )}
          {canWrite && threadRow && (
            <DropdownMenuItem onSelect={() => (row.unread ? onMarkRead(row.entryId) : onMarkUnread(row.entryId))}>
              <CheckCircle size={14} />
              {row.unread ? 'Mark as read' : 'Mark as unread'}
            </DropdownMenuItem>
          )}
          {canWrite && pauseAction && (
            <DropdownMenuItem
              onSelect={() => (paused ? onResumeFollowups(row.entryId) : onPauseFollowups(row.entryId))}
            >
              {paused ? <Play size={14} /> : <Clock size={14} />}
              {paused ? 'Resume follow-ups' : 'Pause follow-ups'}
            </DropdownMenuItem>
          )}
          {canWrite && row.escalatedAt && onClearEscalation && (
            <DropdownMenuItem onSelect={() => onClearEscalation(row.entryId)}>
              <CheckCircle size={14} />
              Clear escalation
            </DropdownMenuItem>
          )}
          {canWrite && onNotUnsubscribe && canNotUnsubscribe(row) && (
            <DropdownMenuItem onSelect={() => onNotUnsubscribe(row.entryId)}>
              <Envelope size={14} />
              Not an unsubscribe
            </DropdownMenuItem>
          )}
          {canWrite && onReopen && (canReopenRow(row) || reopenBlocked !== null) && (
            <DropdownMenuItem
              disabled={reopenBlocked !== null}
              onSelect={() => onReopen(row.entryId)}
              className={reopenBlocked !== null ? REFUSED_ITEM : undefined}
            >
              <ArrowCounterClockwise size={14} />
              Reopen
              {reopenBlocked !== null && <RefusalReason text={reopenBlocked} />}
            </DropdownMenuItem>
          )}
          {canDiscardDraft && liveDraft && (
            <DropdownMenuItem onSelect={() => onDiscardDraft?.(row.entryId, liveDraft.id)}>
              <Trash size={14} />
              Discard draft
            </DropdownMenuItem>
          )}
          {canWrite && canMoveToBrand(row) && (
            <DropdownMenuItem
              disabled={!hasRateToHandOff(row)}
              title={!hasRateToHandOff(row) ? 'Agree a rate first' : undefined}
              onSelect={() => onSendToBrand(row.entryId)}
            >
              <ArrowLineUp size={14} />
              Move to brand dashboard
            </DropdownMenuItem>
          )}
          {/* Figma (row-actions menu, Flow 3): shown for every row — read-only
              staff get it too, same as View profile/Open thread, since it
              doesn't mutate anything. Disabled: no export endpoint exists on
              the BE yet, same "not built yet" treatment as the header's
              disabled Pull creators button. */}
          <DropdownMenuItem disabled className={REFUSED_ITEM}>
            <Export size={14} />
            Export sheet
            <RefusalReason text="Not available yet" />
          </DropdownMenuItem>
          {/* Figma splits the menu above the suppression / delete group. */}
          {(canUnsuppress || canSuppress || canDelete) && <DropdownMenuSeparator />}
          {canUnsuppress && (
            <DropdownMenuItem onSelect={() => onUnsuppress(row.entryId)}>
              <MinusCircle size={14} />
              Remove from suppression
            </DropdownMenuItem>
          )}
          {canSuppress && (
            <DropdownMenuItem onSelect={() => onSuppress(row.entryId)}>
              <Warning size={14} />
              Suppress creator
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem
              className={cn('text-destructive focus:text-destructive', removeBlocked !== null && REFUSED_ITEM)}
              disabled={removeBlocked !== null}
              onSelect={() => onRemove([row.entryId])}
            >
              <Trash size={14} />
              Remove from campaign
              {removeBlocked !== null && <RefusalReason text={removeBlocked} />}
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
